import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchLyrraAccessPadGuideAudio } from '@/lib/lyrra-access-pad-guide.api';
import { resolveUploadsUrl } from '@/lib/resolve-uploads-url';
import { parseSupportedUiLangCode } from '@/lib/ui-languages';
import { LYRRA_ACCESS_PAD_F13_TOGGLE_GUIDE_EVENT } from '@/lib/lyrra-access-pad';

type GuideState = 'idle' | 'loading' | 'ready' | 'playing' | 'error';

/** Langue UI active (alignée sur i18n, comme le badge LyrraPad). */
function resolveGuideLanguage(i18nLang: string | undefined): string {
  return parseSupportedUiLangCode(i18nLang) ?? 'fr';
}

export function useLyrraAccessPadGuideAudio(enabled: boolean) {
  const { i18n } = useTranslation();
  const language = resolveGuideLanguage(i18n.resolvedLanguage ?? i18n.language);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const loadedLangRef = useRef<string | null>(null);
  const togglePlayRef = useRef<() => Promise<void>>(async () => {});
  const [state, setState] = useState<GuideState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const disposeAudio = useCallback(() => {
    const el = audioRef.current;
    if (el) {
      el.pause();
      el.removeAttribute('src');
      el.load();
    }
    audioRef.current = null;
    loadedLangRef.current = null;
  }, []);

  useEffect(() => {
    if (!enabled) {
      disposeAudio();
      setState('idle');
      setErrorMessage(null);
      return;
    }
    if (loadedLangRef.current !== null && loadedLangRef.current !== language) {
      disposeAudio();
      setState('idle');
      setErrorMessage(null);
    }
  }, [enabled, language, disposeAudio]);

  useEffect(() => () => disposeAudio(), [disposeAudio]);

  const ensureAudio = useCallback(async (): Promise<HTMLAudioElement | null> => {
    if (audioRef.current && loadedLangRef.current === language) {
      return audioRef.current;
    }
    if (audioRef.current) {
      disposeAudio();
    }

    setState('loading');
    setErrorMessage(null);
    try {
      const payload = await fetchLyrraAccessPadGuideAudio(language);
      const resolved = resolveUploadsUrl(payload.audioUrl);
      if (!resolved) throw new Error('URL audio invalide');

      const el = new Audio(resolved);
      el.preload = 'auto';
      audioRef.current = el;
      loadedLangRef.current = payload.lang;

      await new Promise<void>((resolve, reject) => {
        const onReady = () => {
          cleanup();
          resolve();
        };
        const onErr = () => {
          cleanup();
          reject(new Error('Impossible de charger le guide audio'));
        };
        const cleanup = () => {
          el.removeEventListener('canplaythrough', onReady);
          el.removeEventListener('error', onErr);
        };
        el.addEventListener('canplaythrough', onReady, { once: true });
        el.addEventListener('error', onErr, { once: true });
        el.load();
      });

      setState('ready');
      return el;
    } catch (e) {
      disposeAudio();
      setState('error');
      setErrorMessage(e instanceof Error ? e.message : 'Erreur audio');
      return null;
    }
  }, [language, disposeAudio]);

  const togglePlay = useCallback(async () => {
    const el = audioRef.current ?? (await ensureAudio());
    if (!el) return;

    if (!el.paused) {
      el.pause();
      setState('ready');
      return;
    }

    try {
      await el.play();
      setState('playing');
    } catch {
      setState('error');
      setErrorMessage('Lecture impossible');
    }
  }, [ensureAudio]);

  togglePlayRef.current = togglePlay;

  useEffect(() => {
    if (!enabled) return;
    const onF13Toggle = () => {
      void togglePlayRef.current();
    };
    window.addEventListener(LYRRA_ACCESS_PAD_F13_TOGGLE_GUIDE_EVENT, onF13Toggle);
    return () => window.removeEventListener(LYRRA_ACCESS_PAD_F13_TOGGLE_GUIDE_EVENT, onF13Toggle);
  }, [enabled]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    const onEnded = () => setState('ready');
    const onPause = () => {
      if (el.currentTime > 0 && !el.ended) setState('ready');
    };
    el.addEventListener('ended', onEnded);
    el.addEventListener('pause', onPause);
    return () => {
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('pause', onPause);
    };
  }, [state]);

  return {
    togglePlay,
    isLoading: state === 'loading',
    isPlaying: state === 'playing',
    errorMessage,
  };
}
